import React, { useEffect, useState, useMemo } from 'react';
// 🔧 Adjust the path to OfficeService based on your folder structure
import OfficeService from '../office/officeService';

const styles = {
  pageWrapper: {
    maxWidth: '900px',
    margin: '0 auto',
    padding: '2rem 1rem',
    fontFamily: `-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif`,
    color: '#111827',
  },
  heading: {
    fontSize: '2rem',
    marginBottom: '0.5rem',
    textAlign: 'center',
  },
  intro: {
    textAlign: 'center',
    marginBottom: '1.5rem',
    color: '#4b5563',
    fontSize: '0.98rem',
  },
  officeList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  officeCard: {
    padding: '1rem 1.25rem',
    borderRadius: '14px',
    border: '1px solid rgba(148,163,184,0.35)',
    boxShadow: '0 6px 14px rgba(15,23,42,0.08)',
    background: '#ffffff',
  },
  officeHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '0.75rem',
    alignItems: 'center',
    marginBottom: '0.5rem',
    flexWrap: 'wrap',
  },
  officeName: {
    fontSize: '1.1rem',
    fontWeight: 700,
  },
  contactLine: {
    margin: '0.1rem 0',
    fontSize: '0.95rem',
    color: '#374151',
  },
  actionsRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.4rem',
    marginTop: '0.4rem',
  },
  btn: {
    padding: '0.4rem 0.9rem',
    borderRadius: '999px',
    border: '1px solid rgba(148,163,184,0.7)',
    background: '#f9fafb',
    fontSize: '0.85rem',
    fontWeight: 600,
    cursor: 'pointer',
    color: '#111827',
    textDecoration: 'none',
    transition: 'all 0.15s ease',
  },
  btnPrimary: {
    background: '#6366f1',
    borderColor: '#6366f1',
    color: '#ffffff',
  },
  btnGhost: {
    background: '#ffffff',
  },
  btnSmallText: {
    fontSize: '0.8rem',
  },
  collapsedHint: {
    fontSize: '0.8rem',
    color: '#6b7280',
    marginTop: '0.25rem',
  },
  formWrapper: {
    marginTop: '0.75rem',
    paddingTop: '0.75rem',
    borderTop: '1px dashed #e5e7eb',
  },
  formRow: {
    marginBottom: '0.6rem',
  },
  label: {
    display: 'block',
    fontSize: '0.85rem',
    fontWeight: 600,
    marginBottom: '0.2rem',
  },
  input: {
    width: '100%',
    padding: '0.4rem 0.55rem',
    borderRadius: '8px',
    border: '1px solid #d1d5db',
    fontSize: '0.9rem',
  },
  textarea: {
    width: '100%',
    padding: '0.4rem 0.55rem',
    borderRadius: '8px',
    border: '1px solid #d1d5db',
    fontSize: '0.9rem',
    minHeight: '80px',
    resize: 'vertical',
  },
  error: {
    color: '#b91c1c',
    fontSize: '0.8rem',
    marginTop: '0.25rem',
  },
  successMsg: {
    color: '#16a34a',
    fontSize: '0.8rem',
    marginTop: '0.25rem',
  },
};

const Contact = () => {
  const [offices, setOffices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openFormOfficeId, setOpenFormOfficeId] = useState(null);
  const [forms, setForms] = useState({});
  const [copiedOfficeId, setCopiedOfficeId] = useState(null);
  const [errorByOffice, setErrorByOffice] = useState({});
  const [successByOffice, setSuccessByOffice] = useState({});

  const officeService = useMemo(() => new OfficeService(), []);

  useEffect(() => {
    (async () => {
      try {
        const data = await officeService.getAllOffices();
        setOffices(data || []);
      } catch (err) {
        console.error('Failed to fetch offices for contact page:', err);
      } finally {
        setLoading(false);
      }
    })();
  }, [officeService]);

  const toggleForm = (officeId) => {
    setErrorByOffice({});
    setSuccessByOffice({});
    setOpenFormOfficeId((prev) => (prev === officeId ? null : officeId));
  };

  const handleFormChange = (officeId, field, value) => {
    setForms((prev) => ({
      ...prev,
      [officeId]: {
        ...(prev[officeId] || {}),
        [field]: value,
      },
    }));
  };

  const handleCopyEmail = async (officeId, email) => {
    if (!email) return;
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(email);
        setCopiedOfficeId(officeId);
        setTimeout(() => setCopiedOfficeId(null), 1500);
      } else {
        // Fallback for very old browsers
        const tempInput = document.createElement('input');
        tempInput.value = email;
        document.body.appendChild(tempInput);
        tempInput.select();
        document.execCommand('copy');
        document.body.removeChild(tempInput);
        setCopiedOfficeId(officeId);
        setTimeout(() => setCopiedOfficeId(null), 1500);
      }
    } catch (err) {
      console.error('Failed to copy email:', err);
    }
  };

  const handleSendEmail = (office) => (e) => {
    e.preventDefault();
    const officeId = office.officeId;
    const form = forms[officeId] || {};
    const name = (form.name || '').trim();
    const fromEmail = (form.fromEmail || '').trim();
    const subject = (form.subject || '').trim();
    const message = (form.message || '').trim();

    // Simple validation
    if (!name || !fromEmail || !message) {
      setErrorByOffice((prev) => ({
        ...prev,
        [officeId]: 'Please fill in your name, email, and message.',
      }));
      setSuccessByOffice((prev) => ({ ...prev, [officeId]: null }));
      return;
    }

    setErrorByOffice((prev) => ({ ...prev, [officeId]: null }));

    const effectiveSubject =
      subject || `Message for ${office.officeName} (via website)`;

    const body = `From: ${name} <${fromEmail}>\n\n${message}`;

    const mailtoUrl = `mailto:${encodeURIComponent(
      office.email || ''
    )}?subject=${encodeURIComponent(effectiveSubject)}&body=${encodeURIComponent(
      body
    )}`;

    // This will open the default mail client (Gmail, Outlook, etc.)
    window.location.href = mailtoUrl;

    setSuccessByOffice((prev) => ({
      ...prev,
      [officeId]: 'Opening your email app with the message pre-filled.',
    }));
  };

  return (
    <div style={styles.pageWrapper}>
      <h1 style={styles.heading}>Contact Us</h1>
      <p style={styles.intro}>
        Reach out to any of our offices directly by phone or email, or use the
        built-in message form to start an email with your details already filled in.
      </p>

      {loading ? (
        <p>Loading office contact information…</p>
      ) : offices.length === 0 ? (
        <p>No office contact information available.</p>
      ) : (
        <div style={styles.officeList}>
          {offices.map((office) => {
            const officeId = office.officeId;
            const form = forms[officeId] || {};
            const isOpen = openFormOfficeId === officeId;
            const officeEmail = office.email || '';

            return (
              <div key={officeId} style={styles.officeCard}>
                <div style={styles.officeHeader}>
                  <div>
                    <div style={styles.officeName}>{office.officeName}</div>
                    {office.phoneNumber && (
                      <p style={styles.contactLine}>
                        <strong>Phone:</strong> {office.phoneNumber}
                      </p>
                    )}
                    {officeEmail && (
                      <p style={styles.contactLine}>
                        <strong>Email:</strong>{' '}
                        <a href={`mailto:${officeEmail}`}>{officeEmail}</a>
                      </p>
                    )}
                  </div>
                </div>

                <div style={styles.actionsRow}>
                  {office.phoneNumber && (
                    <a
                      href={`tel:${office.phoneNumber}`}
                      style={styles.btn}
                    >
                      Call
                    </a>
                  )}
                  {officeEmail && (
                    <>
                      {/* Regular email button using mailto (works in Chrome + others) */}
                      <a
                        href={`mailto:${officeEmail}`}
                        style={{ ...styles.btn, ...styles.btnGhost }}
                      >
                        Email
                      </a>
                      {/* Copy email button */}
                      <button
                        type="button"
                        style={{ ...styles.btn, ...styles.btnSmallText }}
                        onClick={() => handleCopyEmail(officeId, officeEmail)}
                      >
                        {copiedOfficeId === officeId ? 'Copied!' : 'Copy Email'}
                      </button>
                    </>
                  )}
                  {/* Toggle message form */}
                  {officeEmail && (
                    <button
                      type="button"
                      style={{ ...styles.btn, ...styles.btnPrimary }}
                      onClick={() => toggleForm(officeId)}
                    >
                      {isOpen ? 'Hide Message Form' : 'Message This Office'}
                    </button>
                  )}
                </div>

                {officeEmail && !isOpen && (
                  <div style={styles.collapsedHint}>
                    Use <strong>Email</strong> to open your mail app, or{' '}
                    <strong>Message This Office</strong> to pre-fill an email
                    from this page.
                  </div>
                )}

                {isOpen && (
                  <form
                    style={styles.formWrapper}
                    onSubmit={handleSendEmail(office)}
                  >
                    <div style={styles.formRow}>
                      <label style={styles.label} htmlFor={`name-${officeId}`}>
                        Your Name
                      </label>
                      <input
                        id={`name-${officeId}`}
                        type="text"
                        style={styles.input}
                        value={form.name || ''}
                        onChange={(e) =>
                          handleFormChange(officeId, 'name', e.target.value)
                        }
                        placeholder="Your full name"
                      />
                    </div>

                    <div style={styles.formRow}>
                      <label
                        style={styles.label}
                        htmlFor={`email-${officeId}`}
                      >
                        Your Email
                      </label>
                      <input
                        id={`email-${officeId}`}
                        type="email"
                        style={styles.input}
                        value={form.fromEmail || ''}
                        onChange={(e) =>
                          handleFormChange(officeId, 'fromEmail', e.target.value)
                        }
                        placeholder="you@example.com"
                      />
                    </div>

                    <div style={styles.formRow}>
                      <label
                        style={styles.label}
                        htmlFor={`subject-${officeId}`}
                      >
                        Subject (optional)
                      </label>
                      <input
                        id={`subject-${officeId}`}
                        type="text"
                        style={styles.input}
                        value={form.subject || ''}
                        onChange={(e) =>
                          handleFormChange(officeId, 'subject', e.target.value)
                        }
                        placeholder={`Message for ${office.officeName}`}
                      />
                    </div>

                    <div style={styles.formRow}>
                      <label
                        style={styles.label}
                        htmlFor={`message-${officeId}`}
                      >
                        Message
                      </label>
                      <textarea
                        id={`message-${officeId}`}
                        style={styles.textarea}
                        value={form.message || ''}
                        onChange={(e) =>
                          handleFormChange(officeId, 'message', e.target.value)
                        }
                        placeholder="How can we help you?"
                      />
                    </div>

                    <button
                      type="submit"
                      style={{ ...styles.btn, ...styles.btnPrimary }}
                    >
                      Open Email with Message
                    </button>

                    {errorByOffice[officeId] && (
                      <div style={styles.error}>{errorByOffice[officeId]}</div>
                    )}
                    {successByOffice[officeId] && (
                      <div style={styles.successMsg}>
                        {successByOffice[officeId]}
                      </div>
                    )}
                  </form>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Contact;
